import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ChevronUp, ChevronDown, X, Loader2 } from 'lucide-react';

/**
 * FindBar — Ctrl/Cmd+F find-in-document bar. Viewer owns all state (open,
 * query, matchCase, results, activeIndex, searching) and passes it down;
 * this component is the UI + the one place that listens for the Cmd/Ctrl+F
 * shortcut and in-bar navigation keys.
 *
 * It owns its own document-level keydown listener (the CommandPalette
 * pattern) because Viewer's window keydown handler early-returns for
 * INPUT/SELECT/TEXTAREA targets — it can never see keystrokes typed into
 * this bar's input. The listener only ever inspects the 'f'/'F' key (with
 * meta|ctrl) so it never fights other global listeners (e.g. CommandPalette's
 * unconditional-preventDefault Cmd+K handler).
 *
 * Floating card — mounted by Viewer OUTSIDE the scroll container so clicks
 * on it never bubble into ScrollablePages' `[data-page]` background-deselect
 * check (or the large-file single-page `.shadow-lg` click guard).
 */
export default function FindBar({
  open,
  onOpen,
  onClose,
  onNavigate,
  results,
  activeIndex,
  query,
  setQuery,
  matchCase,
  setMatchCase,
  searching,
}) {
  const { t } = useTranslation('viewer');
  const inputRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  const total = results.length;
  const current = total > 0 ? activeIndex + 1 : 0;

  const goNext = () => {
    if (total === 0) return;
    onNavigate((activeIndex + 1) % total);
  };
  const goPrev = () => {
    if (total === 0) return;
    onNavigate((activeIndex - 1 + total) % total);
  };

  // Cmd/Ctrl+F opens the bar; Esc closes it. Single document-level listener,
  // mirroring CommandPalette.jsx's Cmd+K handler.
  useEffect(() => {
    function onKey(e) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        if (!openRef.current) {
          previouslyFocusedRef.current = document.activeElement;
        }
        onOpen();
      } else if (e.key === 'Escape' && openRef.current) {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpen, onClose]);

  // Focus + select-all the input whenever the bar opens; restore prior focus
  // on close.
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    } else if (previouslyFocusedRef.current?.focus) {
      try { previouslyFocusedRef.current.focus(); } catch { /* ignore */ }
    }
  }, [open]);

  if (!open) return null;

  function onInputKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) goPrev(); else goNext();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  const noMatches = total === 0 && query.trim().length > 0 && !searching;

  return (
    <div
      role="search"
      aria-label={t('find.placeholder', 'Find in document')}
      className="absolute top-2 right-4 z-40 flex items-center gap-1 rounded-xl border border-gray-200 bg-white shadow-lg px-2 py-1.5"
    >
      <Search size={16} className="text-gray-400 flex-shrink-0 ml-1" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onInputKeyDown}
        placeholder={t('find.placeholder', 'Find in document')}
        className="w-40 sm:w-56 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 outline-none px-1.5 py-1"
        autoComplete="off"
        spellCheck="false"
      />

      {searching && <Loader2 size={14} className="animate-spin text-gray-400 flex-shrink-0" aria-label={t('find.searching', 'Searching…')} />}

      <span className="text-xs text-gray-500 whitespace-nowrap min-w-[3.5rem] text-center flex-shrink-0">
        {noMatches
          ? t('find.no_matches', 'No matches')
          : total > 0
            ? t('find.of', '{{current}} of {{total}}', { current, total })
            : ''}
      </span>

      <button
        type="button"
        onClick={goPrev}
        disabled={total === 0}
        aria-label={t('find.prev', 'Previous match')}
        className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
      >
        <ChevronUp size={16} />
      </button>
      <button
        type="button"
        onClick={goNext}
        disabled={total === 0}
        aria-label={t('find.next', 'Next match')}
        className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
      >
        <ChevronDown size={16} />
      </button>

      <button
        type="button"
        onClick={() => setMatchCase(!matchCase)}
        aria-pressed={matchCase}
        aria-label={t('find.match_case', 'Match case')}
        title={t('find.match_case', 'Match case')}
        className={`px-1.5 py-1 rounded text-xs font-semibold border focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
          matchCase
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
            : 'border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-100'
        }`}
      >
        Aa
      </button>

      <button
        type="button"
        onClick={onClose}
        aria-label={t('find.close', 'Close')}
        className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
      >
        <X size={16} />
      </button>
    </div>
  );
}
